import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useFetch } from "@/lib/content";
import { useSEO } from "@/lib/seo";
import { buildBookingEmbedUrl, bookingProviderLabel } from "@/lib/booking";
import { Eyebrow } from "@/components/site/Motion";

const STEPS = [
  {
    key: "company_size",
    title: "How big is your company?",
    hint: "Headcount, including founders",
    options: ["1–50", "51–200", "201–500", "500+"],
  },
  {
    key: "revenue_range",
    title: "Where does revenue sit today?",
    hint: "Annual, INR — approximate is fine",
    options: ["Under ₹10 Cr", "₹10–50 Cr", "₹50–200 Cr", "₹200 Cr+", "Prefer not to say"],
  },
  {
    key: "pain_area",
    title: "What's the biggest friction right now?",
    hint: "Pick what hurts most today",
    options: ["Growth has stalled", "Operations are chaotic", "Strategy doesn't execute", "Leadership bandwidth", "Multiple / not sure"],
  },
  {
    key: "timeline",
    title: "When do you want to start?",
    hint: "Be honest — it shapes the plan",
    options: ["Immediately", "This quarter", "In 3–6 months", "Just exploring"],
  },
];

export default function BookCall() {
  const { data: settings } = useFetch("/content/settings");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ company_size: "", revenue_range: "", pain_area: "", timeline: "" });
  const [contact, setContact] = useState({ name: "", email: "", phone: "", company: "" });
  const [website, setWebsite] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useSEO({
    title: "Book a Discovery Call | RenB Solutions",
    description: "Answer four quick questions and book a 15-minute discovery call with RenB Solutions.",
    path: "/book",
  });

  const current = STEPS[step];
  const canContinue = step < 4 ? Boolean(answers[current.key]) : contact.name && contact.email;

  const choose = (option) => {
    setAnswers((a) => ({ ...a, [current.key]: option }));
    setTimeout(() => setStep((s) => Math.min(s + 1, 4)), 220);
  };

  const submit = async () => {
    if (website) return setDone(true);
    setSending(true);
    try {
      await api.post("/leads", {
        ...contact,
        ...answers,
        source: "discovery",
        website,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const calendly = settings?.calendly_url || "https://calendly.com/renb-solutions/discovery-call";

  return (
    <div data-testid="book-call-page" className="mx-auto max-w-[1440px] px-5 pb-24 pt-28 md:px-10 md:pt-36">
      <Toaster richColors position="top-center" />
      <div className="mx-auto max-w-3xl">
        {!done ? (
          <>
            <Eyebrow>Book a discovery call</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.05] tracking-tight md:text-6xl">
              Four questions, then the calendar<span className="text-progress">.</span>
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-obsidian/60">
              These questions make the first 15 minutes useful for both of us — and yes, they filter out
              tyre-kickers. That&apos;s intentional.
            </p>

            <div className="mt-10 flex items-center gap-4" data-testid="book-progress">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">
                {step < 4 ? `0${step + 1} / 04` : "05 / 05 — Your details"}
              </p>
              <div className="h-[3px] flex-1 bg-ash">
                <motion.div
                  className="h-full bg-progress"
                  animate={{ width: `${((step + 1) / 5) * 100}%` }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              {step < 4 ? (
                <motion.div
                  key={`step-${step}`}
                  data-testid={`book-step-${step}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="mt-12"
                >
                  <h2 className="font-serif text-3xl font-medium tracking-tight md:text-4xl">{current.title}</h2>
                  <p className="mt-2 text-sm text-slategray">{current.hint}</p>
                  <div className="mt-8 grid gap-3 sm:grid-cols-2">
                    {current.options.map((opt) => {
                      const active = answers[current.key] === opt;
                      return (
                        <button
                          key={opt}
                          data-testid={`option-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          onClick={() => choose(opt)}
                          className={`border px-6 py-5 text-left text-[15px] font-semibold transition-all duration-200 ${
                            active
                              ? "border-progress bg-progress text-white"
                              : "border-obsidian/15 bg-paper hover:border-obsidian"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {step > 0 && (
                    <button
                      data-testid="book-back"
                      onClick={() => setStep((s) => s - 1)}
                      className="mt-10 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-slategray hover:text-progress"
                    >
                      <ArrowLeft size={14} /> Back
                    </button>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="step-details"
                  data-testid="book-step-details"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="mt-12"
                >
                  <h2 className="font-serif text-3xl font-medium tracking-tight md:text-4xl">And where should we send the confirmation?</h2>
                  <div className="mt-8 grid gap-8 sm:grid-cols-2">
                    <label className="flex flex-col gap-2">
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Name *</span>
                      <input required data-testid="book-name" className="field" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Your full name" />
                    </label>
                    <label className="flex flex-col gap-2">
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Work email *</span>
                      <input required type="email" data-testid="book-email" className="field" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="you@company.com" />
                    </label>
                    <label className="flex flex-col gap-2">
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Phone</span>
                      <input data-testid="book-phone" className="field" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="+91" />
                    </label>
                    <label className="flex flex-col gap-2">
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Company</span>
                      <input data-testid="book-company" className="field" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} placeholder="Company name" />
                    </label>
                    <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                  </div>
                  <div className="mt-10 flex items-center gap-6">
                    <button
                      data-testid="book-submit"
                      disabled={!canContinue || sending}
                      onClick={submit}
                      className="bg-progress px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-progress-dark disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {sending ? "Submitting…" : "See available times"}
                    </button>
                    <button
                      data-testid="book-back-details"
                      onClick={() => setStep(3)}
                      className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-slategray hover:text-progress"
                    >
                      <ArrowLeft size={14} /> Back
                    </button>
                  </div>
                  <p className="mt-6 text-[12px] leading-relaxed text-slategray">
                    Your answers go straight to the RenB partner team. We only use them to prepare for the call.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          <motion.div data-testid="book-success" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-16">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={28} className="text-progress" />
              <h2 className="font-serif text-4xl font-medium tracking-tight md:text-5xl">You&apos;re qualified in. Pick a time.</h2>
            </div>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-obsidian/70">
              Your answers are with the RenB partner team. Below is the live calendar — pick a slot for your
              15-minute discovery call.
            </p>
            <div className="mt-10 border border-ash bg-white" data-testid="calendly-embed">
              <iframe
                src={buildBookingEmbedUrl(calendly, contact)}
                title={`${bookingProviderLabel(calendly)} scheduling`}
                className="h-[720px] w-full"
              />
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
